import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ILeadView } from '../../viewModels/leads/interfaces';
import { queryKeys } from './queryKeys';
import { FocusedLead, fromILeadView } from './types';

interface NextLeadVariables {
	ruleId?: string;
}

/**
 * Pops the next lead out of the queue. Replicates the request shape of
 * `LeadsViewModel.getNext` — a plain `GET /lead?ruleId=...` — without pulling
 * in the MobX viewmodel itself.
 *
 * On success, the new lead is primed into the `focused-view/lead/:companyId`
 * cache so that components reading from `useLead` render immediately without
 * a flash of loading state (see data/README.md).
 *
 * A 404 from the API means "no more leads" — we model that as a successful
 * mutation resolving to `null`.
 */
export function useNextLead(): UseMutationResult<FocusedLead | null, Error, NextLeadVariables | void> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<FocusedLead | null, Error, NextLeadVariables | void>({
		mutationFn: async variables => {
			const ruleId = variables ? variables.ruleId : undefined;
			const result = await userSession.webServiceHelper.callWebServiceAsync<ILeadView>(
				ImpersonationBroker.composeApiUrl({ urlPath: 'lead', queryParams: { ruleId } }),
				'GET'
			);

			if (result.success) {
				return result.value ? fromILeadView(result.value) : null;
			}

			if (result.systemCode === 404) {
				return null;
			}

			throw asApiError(result);
		},
		onSuccess: focusedLead => {
			if (focusedLead?.companyId) {
				queryClient.setQueryData(queryKeys.focusedView.lead(focusedLead.companyId), focusedLead);
			}
		},
	});
}
